/**
 * Cross-origin Optimizely identity hand-off (BYOID) — CompareClub adopter.
 *
 * Shares the Optimizely visitor identity across
 * healthinsurancecomparison.com.au and compareclub.com.au deterministically.
 *
 * Optimizely's built-in cross-origin sync (`waitForOriginSync`) does this via
 * a shared iframe + third-party storage, which Safari ITP and partitioned
 * browsers block — so a large slice of visitors would be assigned a fresh,
 * divergent id here and get re-bucketed. We use BYOID instead: HIC appends its
 * canonical `optimizelyEndUserId` to the CC redirect URL as `?oeu=…` (see
 * utils/optimizely-handoff.js in the hic-optimization repo), and we adopt that
 * id into CC's own `optimizelyEndUserId` cookie *before the snippet loads*, so
 * Optimizely reads it on init and buckets the visitor identically to HIC,
 * regardless of third-party-storage policy.
 *
 * See docs/optimizely-ab-tests.md § Cross-Origin Tracking.
 */

const OEU_COOKIE = 'optimizelyEndUserId'

/** Query-param name carrying the Optimizely end-user id across the hand-off. */
export const OEU_PARAM = 'oeu'

/**
 * The `oeu` value is attacker-controllable (a URL param written into the
 * identity cookie), so only accept the shapes Optimizely actually issues:
 * its own `oeu<timestamp>r<random>` format, or a plain UUID. Anything else is
 * ignored. The length cap is defensive belt-and-braces.
 */
const OEU_MAX_LENGTH = 128
const OEU_PATTERN = /^(oeu\d+r[\d.]+|[0-9a-f-]{36})$/i

function readCookie (name: string): string {
  if (typeof document === 'undefined') return ''
  const match = document.cookie.match(
    new RegExp('(?:^|; )' + name + '=([^;]*)')
  )
  return match ? decodeURIComponent(match[1]) : ''
}

/**
 * Adopt HIC's Optimizely end-user id when arriving via the HIC→CC hand-off.
 *
 * MUST be called before the Optimizely snippet `<script>` is appended so the
 * snippet reads the cookie on initialisation.
 *
 * First-touch-on-CC wins: an existing `optimizelyEndUserId` is never
 * overwritten, so a returning CC visitor is not re-bucketed mid-experiment.
 *
 * @returns true if an incoming id was adopted, false otherwise.
 */
export function adoptCrossOriginVisitorId (): boolean {
  if (typeof window === 'undefined') return false

  const incoming = new URLSearchParams(window.location.search).get(OEU_PARAM)
  if (
    !incoming ||
    incoming.length > OEU_MAX_LENGTH ||
    !OEU_PATTERN.test(incoming)
  ) {
    return false
  }

  // Don't re-bucket a returning CC visitor who already has an id.
  if (readCookie(OEU_COOKIE)) return false

  // Host-only cookie (no `domain` attribute) so it applies on any environment
  // host — compareclub.com.au / develop. / uat. — with nothing hardcoded, and
  // is visible to the snippet's document.cookie read. `Secure` only over
  // HTTPS so local http testing still works.
  const oneYearSeconds = 60 * 60 * 24 * 365
  const secure = window.location.protocol === 'https:' ? '; Secure' : ''
  document.cookie =
    `${OEU_COOKIE}=${encodeURIComponent(incoming)}; path=/; ` +
    `max-age=${oneYearSeconds}; SameSite=Lax${secure}`

  return true
}
